/*
 * Orders — V1.9.1 measured view-model (design handoff README §Screens →
 * 2. Orders; App Shell.dc.html is the measured DOM reference). Pure — the
 * screen (Orders.tsx) renders these; Vitest proves them against the handoff's
 * QA-CHECKLIST assertions.
 *
 * COLOUR SYSTEM (hard rule, QA §2): hue carries ONE meaning.
 *   teal/amber      = state (reserved — never identity)
 *   blue→magenta arc = ticket TYPE (identity — the palette below)
 *   slate            = container boundary
 */
import type { Ticket } from '../../data/types'

// ---------------------------------------------------------------------------
// Ticket-type palette — measured (README §Design tokens → Ticket-type colours).
// Mirrors --tru-ticket-{1..6} in src/theme/tokens.css; the hexes are also
// needed as VALUES here (per-type inline styles + QA 2.2/2.3 assertions).
// No green, amber, teal or red — that separation is the hard rule.
// ---------------------------------------------------------------------------

export interface TypeColour {
  c: string
  tint: string
}

export const TICKET_TYPE_PALETTE: readonly TypeColour[] = [
  { c: '#4fb0e8', tint: 'rgba(79,176,232,.10)' },
  { c: '#7d8cf5', tint: 'rgba(125,140,245,.10)' },
  { c: '#a97ff0', tint: 'rgba(169,127,240,.10)' },
  { c: '#d97fd0', tint: 'rgba(217,127,208,.10)' },
  { c: '#ef7fa8', tint: 'rgba(239,127,168,.10)' },
  { c: '#6a5fd0', tint: 'rgba(106,95,208,.10)' },
]

/** Minimal shape the whole-list derivations need (real Orders pass domain
 *  tickets; tests may pass fixtures). */
export interface OrderTicketsLike {
  tickets: Array<Pick<Ticket, 'typeName' | 'seats'>>
}

/**
 * Type → colour decided ONCE for the whole list, in order of FIRST APPEARANCE
 * across every order (handoff §2 layer 2 + CHANGELOG 1.5): a repeated ticket
 * type is the same colour in EVERY order — colour tells you *what kind of
 * ticket*; the order card tells you *which purchase*. Wraps past 6 types.
 */
export function typeColourMap(orders: OrderTicketsLike[]): Map<string, TypeColour> {
  const map = new Map<string, TypeColour>()
  let n = 0
  for (const o of orders) {
    for (const t of o.tickets) {
      const key = t.typeName || 'Ticket'
      if (!map.has(key)) map.set(key, TICKET_TYPE_PALETTE[n++ % TICKET_TYPE_PALETTE.length])
    }
  }
  return map
}

/**
 * Per-TEAM accent (BOARDROOM-2026-09-09-161918, Duncan customer-report
 * ruling): a card with several team purchases of the same type rendered
 * indistinguishable blocks — the buyer could not tell which tickets form
 * which team. Each team purchase now wears ONE colour, distinct from the
 * adjacent team's: the card's FIRST team keeps its type colour (the shipped
 * canon — colour = ticket type), and every subsequent distinct unit advances
 * the SAME blue→magenta arc by its ordinal, so adjacent teams always differ
 * while the hue-meaning hard rule (no green/amber/teal/red) is preserved by
 * construction. Solos and single-team cards render exactly as before.
 */
export function teamAccent(base: TypeColour, unitOrdinal: number): TypeColour {
  if (unitOrdinal <= 0) return base
  const i = TICKET_TYPE_PALETTE.indexOf(base)
  return TICKET_TYPE_PALETTE[(Math.max(i, 0) + unitOrdinal) % TICKET_TYPE_PALETTE.length]
}

/**
 * Distinct team-purchase ordinals for one card, in block order (0-based).
 * Keyed by teamUnitId; entries without team identity (solos, typeName
 * fallback, degraded my-api) are absent — their blocks keep the type colour.
 */
export function teamUnitOrdinals(tickets: Array<Pick<Ticket, 'teamUnitId'>>): Map<string, number> {
  const map = new Map<string, number>()
  for (const t of tickets) {
    if (t.teamUnitId && !map.has(t.teamUnitId)) map.set(t.teamUnitId, map.size)
  }
  return map
}

/**
 * TEAM-ADJACENT ordering (Duncan iteration BOARDROOM-2026-09-09-182845 on the
 * shipped colours/labels): the per-row wire interleaves same-type units
 * (A1,B1,A2,B2) — a team's members must render SIDE BY SIDE. Groups keyed by
 * teamUnitId (an entry without one is its own singleton group — solos keep
 * their relative position); group order = first appearance; members ordered
 * by teamMemberSeq within a group. Pure + stable, applied by OrderCard BEFORE
 * applyLocalRecords so every index-keyed derivation (seat records, colours,
 * ordinals) sees ONE consistent order.
 */
export function groupTicketsByUnit<T extends Pick<Ticket, 'teamUnitId' | 'teamMemberSeq'>>(tickets: T[]): T[] {
  const order: string[] = []
  const groups = new Map<string, T[]>()
  tickets.forEach((t, i) => {
    const k = t.teamUnitId ?? `solo:${i}`
    if (!groups.has(k)) {
      groups.set(k, [])
      order.push(k)
    }
    groups.get(k)!.push(t)
  })
  return order.flatMap((k) => {
    const g = groups.get(k)!
    return g.length > 1 ? [...g].sort((a, b) => (a.teamMemberSeq ?? 0) - (b.teamMemberSeq ?? 0)) : g
  })
}

/**
 * Desktop seat-tag gutter width — decided once for the WHOLE list, never per
 * card (QA 3.8 / CHANGELOG cross-cutting rule 3): 56px if ANY ticket anywhere
 * in the list has more than one place, else 0. A fixed track — never
 * `max-content`, which collapses to 0 on rows without a tag and shifts the
 * text origin (QA 3.7).
 */
export function tagColWidth(orders: OrderTicketsLike[]): 56 | 0 {
  return orders.some((o) => o.tickets.some((t) => t.seats.length > 1)) ? 56 : 0
}

/**
 * Seat identity — `{orderRef}|{ticketIndex}.{seatIndex}` (0-based indices, as
 * measured in the prototype). The ORDER REFERENCE is part of the key: without
 * it, ticket 0 / seat 0 of every order collides and one assignment writes into
 * three orders (CHANGELOG 1.10 — the found-late bug).
 */
export function seatKeyFor(orderRef: string, ticketIndex: number, seatIndex: number): string {
  return `${orderRef}|${ticketIndex}.${seatIndex}`
}
